import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Logo, LogoMark, BRAND_NAME, BRAND_TAGLINE } from '../components/Logo';

const MicrosoftLogo: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const Login: React.FC = () => {
  const { login } = useAuth();
  const { darkMode } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await login();
    } catch (err) {
      setError('Sign-in failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-dvh flex items-center justify-center p-4 ${
        darkMode ? 'bg-gray-950' : 'bg-ground'
      }`}
    >
      <div className="w-full max-w-sm">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 text-center">
          <h1 className="flex justify-center" aria-label={BRAND_NAME}>
            <Logo variant="onLight" wordmarkSize={32} />
          </h1>

          <div className="mt-6 mb-8">
            <p className="font-display text-lg font-bold text-ink dark:text-gray-100">
              {BRAND_TAGLINE}
            </p>
            <p className="text-sm text-muted dark:text-gray-400 mt-2 leading-relaxed">
              Schedule intelligence for general contractors and project managers.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-crit-bg border border-crit-text rounded-[8px]">
              <p className="text-sm text-crit-text">{error}</p>
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-accent hover:bg-link disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-[8px] transition-colors min-h-[52px] shadow-sm"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Signing in…</span>
              </>
            ) : (
              <>
                <MicrosoftLogo />
                <span>Sign in with Microsoft</span>
              </>
            )}
          </button>

          <p className="text-xs text-muted dark:text-gray-500 mt-6">
            Sign in with your company Microsoft account.
            <br />
            Contact your administrator if you need access.
          </p>
        </div>

        <p className="flex items-center justify-center gap-2 text-xs text-muted dark:text-gray-600 mt-6">
          <LogoMark variant="onLight" height={14} />
          © {new Date().getFullYear()} {BRAND_NAME}
        </p>
      </div>
    </div>
  );
};

export default Login;
